import {
  useCallback,
  useContext,
  useMemo,
  memo,
  type FC,
  type CSSProperties,
} from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { PapiDeviation } from '@wix/da-papi-types';
import {
  BiData,
  DreamupSubmitAsDeviationBiEvent,
  OpenPromptModalBiEvent,
} from '@wix/da-bi/pkg/events';
import { Url } from '@wix/da-url';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import UploadIcon from '@wix/da-ds/pkg/Icons/24x24/Upload';
import TrashcanIcon from '@wix/da-ds/pkg/Icons/24x24/Trashcan';
import DownloadIcon from '@wix/da-ds/pkg/Icons/24x24/Download';
import ImageIcon from '@wix/da-ds/pkg/Icons/24x24/Image';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import ReusePromptIcon from '@wix/da-ds/pkg/Icons/24x24/ReusePrompt';
import CreateVariationsIcon from '@wix/da-ds/pkg/Icons/24x24/CreateVariations';
import ExpandIcon from '@wix/da-ds/pkg/Icons/Expand';
import RemoveBackgroundIcon from '@wix/da-ds/pkg/Icons/24x24/RemoveBackground';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import MediaIndicator from '@wix/da-ds/pkg/thumbs/MediaIndicator';
import Dumbo from '@wix/da-shared-react/pkg/DeviationViews/Dumbo';
import HoverOnly from '@wix/da-ds/pkg/thumbs/OverlayHelpers/HoverOnly';
import ThumbOverlayBackground from '@wix/da-ds/pkg/thumbs/legos/ThumbOverlayBackground';
import ThumbActions from '@wix/da-shared-react/pkg/DeviationViews/Helper/ThumbActions';
import IconButtonWithBackground from '@wix/da-ds/pkg/Buttons/IconButton/IconButtonWithBackground';
import HoverableWrapper from '@wix/da-ds/pkg/thumbs/OverlayHelpers/HoverableWrapper';
import {
  fetchExtendedDeviation,
  pushPromptInfoModal,
} from '../../../redux/dreamup/actions';
import { getPrompt } from '@wix/da-dreamup/pkg/redux/form/selectors';
import {
  useCopyPromptAction,
  useCreateVariationAction,
  useDownloadAction,
  useDreamupDeleteAction,
  useMuroAction,
  useRemoveBackgroundAction,
  useUpscaleAction,
} from '../hooks';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import {
  usePushMiniMobileMuroModal,
  usePushMiniMuroModal,
} from '@wix/da-shared-react/pkg/utils/hooks/usePushMuroModal';

import s from './DreamupThumb.scss';

interface Props {
  deviation: PapiDeviation;
  width?: number;
  height?: number;
  style?: CSSProperties;
  className?: string;
}

/**
 * A deviation thumbnail prepopulated with the `actions` required by Dreamup.
 */
const DreamupThumb: FC<Props> = ({
  deviation,
  width,
  height,
  style,
  className,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const dispatchPushPromptInfoModal = useAction(pushPromptInfoModal);
  const dispatchFetchExtendedDeviation = useAction(fetchExtendedDeviation);
  const upscaleAction = useUpscaleAction(deviation, 'main');
  const removeBackgroundAction = useRemoveBackgroundAction(deviation, 'main');
  const copyPromptAction = useCopyPromptAction(deviation);
  const muroAction = useMuroAction(deviation);
  const downloadAction = useDownloadAction(deviation);
  const prompt = useSelector(getPrompt);
  const createVariationAction = useCreateVariationAction(deviation);

  const pushMiniMuroModal = usePushMiniMuroModal(deviation);
  const pushMobileMuroModal = usePushMiniMobileMuroModal(deviation);

  const handlePushDeleteConfirmation = useDreamupDeleteAction(deviation, {
    biData: {
      sectionname: '3_dots',
    },
  });

  const handleThumbClick = useCallback(() => {
    dispatchPushPromptInfoModal(deviation);
  }, [dispatchPushPromptInfoModal, deviation]);

  const handleActionsOpen = useCallback(() => {
    dispatchFetchExtendedDeviation(deviation);
  }, [dispatchFetchExtendedDeviation, deviation]);

  const actions: NullableDropdownItem[] = useMemo(
    () => [
      {
        label: '',
        items: [
          {
            label: t('dreamup.content.deviationThumb.actions.submit'),
            icon: <UploadIcon disableDefaultColors size={IconSize.SMALL} />,
            link: Url.submissionLinkWithId(deviation.deviationId),
            biData: BiData<DreamupSubmitAsDeviationBiEvent>({
              evid: 910,
              sectionname: '3_dots',
              deviationid: deviation.deviationId,
              prompt,
            }),
          },
        ],
      },
      {
        label: '',
        items: [
          {
            icon: <DownloadIcon disableDefaultColors size={IconSize.SMALL} />,
            label: t('dreamup.content.deviationThumb.actions.download'),
            link: downloadAction.href,
            disabled: downloadAction.disabled,
            biData: downloadAction.biData,
          },
          {
            label: t('dreamup.content.deviationThumb.actions.copyPrompt'),
            icon: (
              <ReusePromptIcon disableDefaultColors size={IconSize.SMALL} />
            ),
            onClick: copyPromptAction.onClick,
            disabled: copyPromptAction.disabled,
            biData: copyPromptAction.biData,
          },
          {
            label: t('dreamup.content.deviationThumb.actions.createVariations'),
            icon: (
              <CreateVariationsIcon
                disableDefaultColors
                size={IconSize.SMALL}
              />
            ),
            onClick: createVariationAction.onClick,
            disabled: createVariationAction.disabled,
            biData: createVariationAction.biData,
          },
          {
            label: t('dreamup.content.deviationThumb.actions.upscale'),
            icon: <ExpandIcon disableDefaultColors size={IconSize.SMALL} />,
            onClick: upscaleAction.onClick,
            disabled: upscaleAction.disabled,
            biData: upscaleAction.biData,
          },
          !isMobile && {
            label: muroAction.label,
            icon: <ImageIcon disableDefaultColors size={IconSize.SMALL} />,
            onClick: () => {
              pushMiniMuroModal();
            },
            disabled: muroAction.disabled,
            target: '_blank',
            biData: muroAction.biData,
          },
          isMobile && {
            label: muroAction.label,
            icon: <ImageIcon disableDefaultColors size={IconSize.SMALL} />,
            onClick: () => {
              pushMobileMuroModal();
            },
            target: '_blank',
            biData: muroAction.biData,
          },
          {
            label: t('dreamup.content.deviationThumb.actions.removeBackground'),
            icon: (
              <RemoveBackgroundIcon
                disableDefaultColors
                size={IconSize.SMALL}
              />
            ),
            onClick: removeBackgroundAction.onClick,
            disabled: removeBackgroundAction.disabled,
            biData: removeBackgroundAction.biData,
          },
          {
            label: t('dreamup.content.deviationThumb.actions.delete'),
            icon: <TrashcanIcon disableDefaultColors size={IconSize.SMALL} />,
            onClick: handlePushDeleteConfirmation,
          },
        ],
      },
    ],
    [
      t,
      deviation.deviationId,
      prompt,
      downloadAction.href,
      downloadAction.disabled,
      downloadAction.biData,
      copyPromptAction.onClick,
      copyPromptAction.disabled,
      copyPromptAction.biData,
      createVariationAction.onClick,
      createVariationAction.disabled,
      createVariationAction.biData,
      upscaleAction.onClick,
      upscaleAction.disabled,
      upscaleAction.biData,
      isMobile,
      muroAction.label,
      muroAction.disabled,
      muroAction.biData,
      removeBackgroundAction.onClick,
      removeBackgroundAction.disabled,
      removeBackgroundAction.biData,
      handlePushDeleteConfirmation,
      pushMiniMuroModal,
      pushMobileMuroModal,
    ]
  );

  return (
    <HoverableWrapper
      style={{ width, height, ...style }}
      className={c(s['root'], className)}
    >
      <PlainButtonOrLink
        className={s['thumb-button']}
        onClick={handleThumbClick}
        biData={BiData<OpenPromptModalBiEvent>({
          evid: 921,
          deviationid: deviation.deviationId,
        })}
        isFakeButton
      >
        <Dumbo deviation={deviation} width={width} height={height} />
      </PlainButtonOrLink>
      <HoverOnly>
        <ThumbOverlayBackground type="gradient" className={s['overlay']} />
        <div className={s['title']}>{deviation.title}</div>
      </HoverOnly>
      <div className={s['top-right']}>
        {deviation.isUpscaled && <MediaIndicator type="hd" />}
        <ThumbActions
          deviation={deviation}
          actions={actions}
          onOpen={handleActionsOpen}
        >
          <IconButtonWithBackground
            size="medium"
            icon={EllipsisIcon}
            aria-label={t(
              'dreamup.content.deviationThumb.actions.a11y.moreActions'
            )}
          />
        </ThumbActions>
      </div>
    </HoverableWrapper>
  );
};

DreamupThumb.displayName = 'DreamupThumb';

export default memo(DreamupThumb, isEqual);
